import { Zap, Mail, ShieldCheck } from "lucide-react";

/**
 * "Why members choose BBK" — three parallel value-prop cards on the public landing.
 * iter_142 — swapped the phone-based consultation card to email-based scheduling
 * via support@bulkbuykits.com. Compliance-safe, easier to triage, and matches how
 * research buyers actually prefer to schedule.
 */

const CARDS = [
  {
    key: "no-waits",
    icon: Zap,
    title: "No three-week waits.",
    body: "Kits ship from our two U.S. warehouses, so stock hits your bench in days — not weeks tied up in transit.",
    chip: "3–5 day delivery",
  },
  {
    key: "consultations",
    icon: Mail,
    title: "Consultations by request.",
    body: "Talk to a human, not a chatbot. Email support to schedule a call for dosing questions, kit selection, or bulk-order logistics.",
    isEmailCard: true,
    chip: "U.S.-based support",
  },
  {
    key: "no-customs",
    icon: ShieldCheck,
    title: "No Customs seizures.",
    body: "Domestic fulfillment end-to-end — nothing crosses a border between our warehouses and your address.",
    chip: "Domestic only",
  },
];

export default function LandingWhySection() {
  return (
    <section
      className="relative bg-bone py-20 sm:py-28 lg:py-32"
      data-testid="landing-why"
      aria-label="Why members choose Bulk Buy Kits"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Section eyebrow + heading */}
        <div className="max-w-3xl">
          <p className="text-[13px] uppercase tracking-[0.32em] text-oxblood font-mono">
            Why members stay
          </p>
          <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal tracking-tight leading-[1.02]">
            Domestic. Direct.
            <br />
            <span className="italic text-charcoal/85">Answered by a human.</span>
          </h2>
        </div>

        {/* 3-card grid */}
        <div className="mt-14 sm:mt-16 grid md:grid-cols-3 gap-6 lg:gap-8">
          {CARDS.map(({ key, isEmailCard, ...rest }) => (
            <WhyCard key={key} {...rest} showEmail={isEmailCard} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyCard({ icon: Icon, title, body, showEmail, chip }) {
  return (
    <article
      className="group relative bg-white border border-borderSubtle rounded-[6px] p-8 sm:p-10 flex flex-col transition-shadow duration-500 hover:shadow-[0_30px_60px_-40px_rgba(26,26,26,0.20)]"
      data-testid="landing-why-card"
    >
      {/* Corner cropmarks — match the stat box aesthetic */}
      <CornerMarks />

      {/* Icon tile */}
      <div
        className="relative w-14 h-14 rounded-[8px] grid place-items-center mb-8"
        style={{
          background:
            "linear-gradient(135deg, rgba(74,14,23,0.08), rgba(140,123,102,0.06))",
        }}
        aria-hidden
      >
        <Icon size={22} strokeWidth={1.5} className="text-oxblood" />
      </div>

      {/* Title */}
      <h3 className="font-serif text-2xl sm:text-3xl text-charcoal tracking-tight leading-[1.05]">
        {title}
      </h3>

      {/* Body */}
      <p className="mt-4 text-sm sm:text-base text-charcoal/85 leading-relaxed flex-1">
        {body}
      </p>

      {/* Support email (Consultations card only) */}
      {showEmail && (
        <a
          href="mailto:support@bulkbuykits.com?subject=Consultation%20request"
          className="mt-5 inline-flex items-baseline gap-2 font-mono text-[15px] sm:text-base text-charcoal hover:text-oxblood transition-colors break-all"
          data-testid="landing-why-support-email"
        >
          support@bulkbuykits.com
        </a>
      )}

      {/* Proof chip */}
      <div className="mt-8">
        <span className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-bone border border-borderSubtle text-[12px] uppercase tracking-[0.28em] text-oxblood font-mono">
          {chip}
        </span>
      </div>
    </article>
  );
}

function CornerMarks() {
  const stroke = "rgba(26,26,26,0.22)";
  const size = 10;
  const Mark = ({ style }) => (
    <span className="absolute" style={style} aria-hidden>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <path d={`M0 0 H${size} M0 0 V${size}`} stroke={stroke} strokeWidth="1" />
      </svg>
    </span>
  );
  return (
    <>
      <Mark style={{ top: 10, left: 10 }} />
      <Mark style={{ top: 10, right: 10, transform: "scaleX(-1)" }} />
      <Mark style={{ bottom: 10, left: 10, transform: "scaleY(-1)" }} />
      <Mark style={{ bottom: 10, right: 10, transform: "scale(-1,-1)" }} />
    </>
  );
}
